/**
 * Dokumentation der Planungsphase — die Dokumentbeschreibung, KEIN Renderer.
 *
 * Muster wie `bauordnung/dokument/dokumentbeschreibung.ts`: hier wird
 * entschieden, WAS im Dokument steht (Gliederung, Wortlaut, Tabellen), der
 * Renderer `services/pdf/generatorPlanungsdokumentation.ts` zeichnet nur.
 * Geprueft wird die Beschreibung, nicht die PDF-Bytes. Diese Datei importiert
 * nichts aus `jspdf`.
 *
 * Die Fachregeln werden NICHT nachgebaut, sondern gelesen: Pflichten aus
 * `pflichtenStand` (live aus den Merkmalen, dreiwertig), Anhang II aus
 * `schwellenwerteAusMerkmalen`, Kennzahlen aus `berechneKennzahlen`, die
 * offenen Punkte aus `restrisikoliste`, das Freigabe-Gate aus
 * `pruefeFreigabe`, die Unterlagen aus `baueUnterlagenZeilen`.
 *
 * ─── Hausregeln, die hier festgenagelt sind ───────────────────────────────
 *   - „Noch nicht geklaert" ist NIE „nicht erforderlich". Der Satz steht im
 *     Text von Abschnitt 2.2 und in der Vorbemerkung.
 *   - Ein fehlender Wert wird benannt („nicht angegeben", „nicht geklärt"),
 *     nie als Strich gedruckt (`services/pdf/fehlendeAngabe.ts`).
 *   - Ein leerer Abschnitt bleibt stehen und sagt in einem Satz, dass er leer
 *     ist. Ein fehlender Abschnitt liest sich als „vergessen".
 *   - Die interne `bewertung` einer Frage steht NUR mit
 *     `mitInternerBewertung` im Dokument: die Dokumentation kann nach aussen
 *     gehen, die Bewertung ist die Einschaetzung des Koordinators.
 *   - Als Projektmerkmale erscheinen NUR die Schluessel aus `MERKMAL_GRUPPEN`.
 *     In `merkmale` koennen kuenftig nicht-fachliche Vermerke liegen.
 *
 * ─── Schreibweise der Katalogtexte ────────────────────────────────────────
 * Fragenkatalog, Kapiteltitel und die Begruendungen aus `planungLogic.ts`
 * sind im Repo in Umschrift geschrieben („Gefaehrdung", „ueber"). In einem
 * Dokument, das wie ein Gutachten aussehen soll, ist das ein Fremdkoerper.
 * `umlauteSetzen` stellt die Umlaute fuer DIESE Texte typografisch her; der
 * gespeicherte Wortlaut bleibt unberuehrt. Freitext der Nutzer (Antworten,
 * Baubeschreibung, eigene Fragen, Bemerkungen) wird NIE umgeschrieben.
 */

import { LIEFERANT_TITEL } from '../../../services/mitgeltendeDokumente';
import { quelleText } from '../antwortQuelle';
import { ADRESSAT_LABEL, GEBAEUDETYP_LABEL, KAPITEL, MERKMAL_LABEL } from '../fragenkatalog';
import { MERKMAL_GRUPPEN } from '../grundlagen/merkmalGruppen';
import { vorankuendigungFrist } from '../planungAmpel';
import {
    berechneKennzahlen,
    dritterText,
    gebaeudetypAus,
    gruppiereNachKapitel,
    HINWEIS_ART_LABEL,
    istUeberfaellig,
    pflichtenStand,
    pruefeFreigabe,
    restrisikoliste,
    schwellenwerteAusMerkmalen,
    STATUS_LABEL,
} from '../planungLogic';
import type {
    FrageAdressat,
    KatalogKapitel,
    PflichtArt,
    PflichtStand,
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
    PlanungUnterlage,
    ProjektMerkmale,
    SigeSchrittDokumente,
} from '../types';
import {
    baueUnterlagenZeilen,
    datumDeutsch,
    merkmalText,
    UNTERLAGEN_STATUS_LABEL,
    unterlagenFristUeberfaellig,
    type UnterlagenZeile,
} from '../unterlagenLogic';

// ═══════════════════════════════════════════════════════════════════════════
// SCHNITTSTELLE
// ═══════════════════════════════════════════════════════════════════════════

export interface DokumentProjekt {
    name?: string;
    nummer?: string;
    /** Anschrift der Baustelle. */
    adresse?: string;
    bauherr?: string;
}

/**
 * Ob der SiGe-Plan beim Druck gelesen wurde:
 *   gelesen      — der aktuelle Plan ist gelesen, `sigeSchritte` sind seine
 *                  mitgeltenden Dokumente (auch leer: der Plan nennt keine)
 *   keiner       — das Projekt hat keinen SiGe-Plan
 *   nicht_lesbar — es sollte gelesen werden, ging aber nicht
 * Nur mit `gelesen` behauptet Abschnitt 5.1 einen Bezug auf den SiGe-Plan.
 */
export type SigePlanLage = 'gelesen' | 'keiner' | 'nicht_lesbar';

/**
 * Was die Lesung des SiGe-Plans (`fetchSigePlanDokumente`, dieselbe
 * Lesefunktion wie `useSigePlanDokumente`) fuer das Dokument bedeutet.
 * `null`: nicht gelesen (etwa weil der Aufruf selbst scheiterte).
 */
export const sigePlanFuerDokument = (
    lesung: { planId: string | null; schritte: SigeSchrittDokumente[]; lesefehler: string | null } | null,
): { sigePlan: SigePlanLage; sigeSchritte: SigeSchrittDokumente[] } => {
    if (!lesung || lesung.lesefehler) return { sigePlan: 'nicht_lesbar', sigeSchritte: [] };
    if (!lesung.planId) return { sigePlan: 'keiner', sigeSchritte: [] };
    return { sigePlan: 'gelesen', sigeSchritte: lesung.schritte || [] };
};

/**
 * Alles, was die Dokumentation liest. Dieselben Quellen wie die Ampel
 * (`AmpelEingang`), damit Ampel und Dokument dasselbe sagen.
 */
export interface DokumentEingang {
    phase: Planungsphase;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
    /** Gespeicherter Stand der Unterlagen-Checkliste (`phase.unterlagen`). */
    unterlagen: PlanungUnterlage[];
    /** Ob der SiGe-Plan gelesen wurde (`sigePlanFuerDokument`). Pflicht:
     *  wer das Dokument baut, muss es sagen — 5.1 nennt den SiGe-Plan nur,
     *  wenn er wirklich gelesen ist. */
    sigePlan: SigePlanLage;
    /** Mitgeltende Dokumente der Arbeitsschritte des aktuellen SiGe-Plans.
     *  Zaehlen nur mit `sigePlan: 'gelesen'`; sonst gelten allein die aus den
     *  Merkmalen erforderlichen Unterlagen. */
    sigeSchritte?: SigeSchrittDokumente[];
    projekt: DokumentProjekt;
    /** Name des Koordinators, der die Dokumentation zeichnet. */
    bearbeiter: string;
    /** Heutiger Tag in Ortszeit (`heuteLokalIso`), YYYY-MM-DD. Der Stand. */
    heuteIso: string;
    /** Ausfuehrungsbeginn (Rueckfall Projektstart) — bestimmt der Aufrufer. */
    baustartIso: string | null;
    /** Titel der Belege in der Ablage (`project_documents.id` → Titel), fuer
     *  die Quellenzeile der Antworten. Fehlt er, steht „Beleg in der Ablage". */
    belegTitel?: Record<string, string>;
}

export interface DokumentOptionen {
    /** Interne Bewertung des Koordinators mitdrucken. Vorgabe: aus. */
    mitInternerBewertung?: boolean;
}

export interface Angabe {
    bezeichnung: string;
    wert: string;
}

export interface TabellenSpalte {
    titel: string;
    /** Feste Breite in mm. Ohne Angabe teilt sich die Spalte den Rest. */
    breite?: number;
    /** Anteil am Rest fuer Spalten ohne `breite` (Vorgabe 1). */
    gewicht?: number;
    ausrichtung?: 'links' | 'mitte' | 'rechts';
}

export type DokumentBlock =
    | { art: 'absatz'; text: string }
    | { art: 'angaben'; zeilen: Angabe[] }
    | { art: 'tabelle'; spalten: TabellenSpalte[]; zeilen: string[][] }
    /** Zeichnungsfeld: je Feld eine Linie, darunter die Beschriftung. */
    | { art: 'zeichnung'; felder: { beschriftung: string; name?: string }[] };

export interface DokumentAbschnitt {
    /** Gliederungsnummer, z. B. „4" oder „4.2". */
    nummer: string;
    titel: string;
    bloecke: DokumentBlock[];
    unterabschnitte: DokumentAbschnitt[];
}

export interface DokumentModell {
    titel: string;
    untertitel: string;
    /** Bezeichnung des Bauvorhabens fuer die Kopfzeile ab Seite 2. */
    bauvorhaben: string;
    /** Stand als TT.MM.JJJJ — Titelei und Fusszeile. */
    standDatum: string;
    titelei: Angabe[];
    vorbemerkung: string[];
    mitInternerBewertung: boolean;
    abschnitte: DokumentAbschnitt[];
}

export const DOKUMENT_TITEL = 'Dokumentation der Planungsphase';
export const DOKUMENT_UNTERTITEL = 'Sicherheits- und Gesundheitsschutzkoordination nach § 3 Abs. 2 BaustellV';

/** Wortlaut fuer fehlende Angaben — Hausregel „benennen, nie Strich". */
export const NICHT_ANGEGEBEN = 'nicht angegeben';
export const NICHT_GEKLAERT = 'nicht geklärt';

// ═══════════════════════════════════════════════════════════════════════════
// SCHREIBWEISE DER KATALOGTEXTE
// ═══════════════════════════════════════════════════════════════════════════

const UMLAUT: Record<string, string> = { a: 'ä', o: 'ö', u: 'ü', A: 'Ä', O: 'Ö', U: 'Ü' };

/**
 * Darf das „ae/oe/ue" an Stelle `i` des Wortes zum Umlaut werden?
 *
 * Die Ausnahmen sind am geschlossenen Bestand geprueft (Fragenkatalog und
 * Begruendungen der Pflichtpruefung, siehe Test): „aue"/„eue" (Bauen, Dauer,
 * Feuerwehr, neue), „que" (Quelle), „-uell" (aktuell, manuell, individuell),
 * „zuerst"/„zueinander", Wortende auf „oe" (Aloe), „Aero-", „-ael" (Namen).
 */
const darfUmlaut = (wort: string, i: number): boolean => {
    const w = wort.toLowerCase();
    const c = w[i];
    const vor = i > 0 ? w[i - 1] : '';
    if (c === 'u') {
        if (vor === 'a' || vor === 'e' || vor === 'q') return false;
        if (i === 1 && /^zu(er|ein)/.test(w)) return false;
        if (w.slice(i, i + 4) === 'uell' && /[tndsx]/.test(vor)) return false;
        return true;
    }
    if (c === 'a') {
        if (i === 0 && w.startsWith('aero')) return false;
        if (i === w.length - 3 && w.endsWith('ael')) return false;
        return true;
    }
    // c === 'o'
    if (vor === 'o') return false;
    if (i === w.length - 2) return false;
    if (/^poe|koeffizient/.test(w.slice(Math.max(0, i - 1)))) return false;
    return true;
};

/** ss → ß nur fuer belegte Wortstaemme. Eine Regel „ss nach langem Vokal"
 *  ist ohne Woerterbuch nicht zu haben; die Liste deckt den Katalog ab. */
const ESZETT: ReadonlyArray<[RegExp, string]> = [
    [/(m)assnahm/gi, '$1aßnahm'],
    [/(m)asshalt/gi, '$1aßhalt'],
    [/(grubenm)asse/gi, '$1aße'],
    [/(s)trass(?=e)/gi, '$1traß'],
    [/(schl)iess/gi, '$1ieß'],
    [/(fl)iess/gi, '$1ieß'],
    [/(sp)iess/gi, '$1ieß'],
    [/(m)ässig/gi, '$1äßig'],
    [/(gem)äss/gi, '$1äß'],
    [/(g)ross/gi, '$1roß'],
    [/(g)röss/gi, '$1röß'],
    [/^(f)uss/gi, '$1uß'],
    [/^(a)uss(?=er|en(?!d))/gi, '$1uß'],
    [/(ä)uss/gi, '$1uß'],
    [/^(h)eiss/gi, '$1eiß'],
    [/^(w)eiss/gi, '$1eiß'],
    [/(schw)eiss/gi, '$1eiß'],
    [/(dr)aussen/gi, '$1außen'],
    [/(b)loss/gi, '$1loß'],
    [/(st)oss/gi, '$1oß'],
    [/(b)ussgeld/gi, '$1ußgeld'],
];

const wortMitUmlauten = (wort: string): string => {
    // Kuerzel in Grossbuchstaben (AE, OE) bleiben, wie sie sind.
    if (wort.length > 1 && wort === wort.toUpperCase()) return wort;
    let aus = '';
    for (let i = 0; i < wort.length; i++) {
        const c = wort[i];
        if (wort[i + 1] === 'e' && UMLAUT[c] && darfUmlaut(wort, i)) {
            aus += UMLAUT[c];
            i++;
            continue;
        }
        aus += c;
    }
    return ESZETT.reduce((s, [muster, ersatz]) => s.replace(muster, ersatz), aus);
};

/**
 * Stellt in Katalog- und Systemtexten die Umlaute her („Gefaehrdung" →
 * „Gefährdung", „Massnahmen" → „Maßnahmen"). Idempotent: ein Text, der die
 * Umlaute schon traegt, bleibt unveraendert.
 */
export const umlauteSetzen = (text: string | null | undefined): string =>
    String(text || '').replace(/[A-Za-zÄÖÜäöüß]+/g, wortMitUmlauten);

/** Deutsche Anfuehrungszeichen statt der geraden aus der Umschrift:
 *  "in eigener Verantwortung" → „in eigener Verantwortung“. */
const anfuehrungszeichen = (text: string): string =>
    text.replace(/„([^"“”]*)"/g, '„$1“').replace(/"([^"]*)"/g, '„$1“');

/** Katalogtext (Frage, Kapitel, Anhang II): Umlaute, Anfuehrungszeichen und
 *  der deutsche Gedankenstrich (Halbgeviert „–") statt des englischen
 *  Geviertstrichs „—" aus dem Katalog. Der Wortlaut selbst bleibt — er ist
 *  das, was gefragt wurde. */
export const katalogText = (text: string | null | undefined): string =>
    anfuehrungszeichen(umlauteSetzen(text)).replace(/\s*—\s*/g, ' – ');

/**
 * Systemtext fuer den Fliesstext eines Gutachtens: Umlaute, Anfuehrungs-
 * zeichen, Dezimalkomma und KEINE Gedankenstrich-Einschuebe. Die
 * Begruendungen aus `planungLogic.ts` verbinden Satzteile mit „ — ": eine
 * angehaengte Fundstelle („ — § 2 Abs. 2 BaustellV.") kommt in Klammern, vor
 * einem Grossbuchstaben beginnt ein neuer Satz, sonst steht ein Komma.
 */
export const systemText = (text: string | null | undefined): string =>
    anfuehrungszeichen(umlauteSetzen(text))
        .replace(/(\d)\.(\d+)(?=\s?m\b)/g, '$1,$2')
        .replace(/\s+[—–]\s+(§\s[^—–]*?BaustellV)\./g, ' ($1).')
        .replace(/\s+[—–]\s+(?=[A-ZÄÖÜ])/g, '. ')
        .replace(/\s+[—–]\s+/g, ', ')
        .replace(/\.\s*\./g, '.')
        .replace(/\s{2,}/g, ' ')
        .trim();

// ═══════════════════════════════════════════════════════════════════════════
// KLEINKRAM
// ═══════════════════════════════════════════════════════════════════════════

const einzeilig = (v: string | null | undefined): string =>
    String(v || '').replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim();

const oder = (v: string | null | undefined, ersatz: string): string => einzeilig(v) || ersatz;

/** Freitext mit Absaetzen: Leerzeilen trennen Absaetze, einfache
 *  Zeilenumbrueche bleiben stehen (Aufzaehlungen der Nutzer). */
const absaetzeAus = (text: string | null | undefined): string[] =>
    String(text || '')
        .replace(/\r\n/g, '\n')
        .split(/\n\s*\n/)
        .map(a => a.split('\n').map(z => z.trimEnd()).join('\n').trim())
        .filter(Boolean);

const absatz = (text: string): DokumentBlock => ({ art: 'absatz', text });

const zahlText = (n: unknown, einheit = ''): string => {
    if (typeof n !== 'number' || !isFinite(n) || n < 0) return NICHT_ANGEGEBEN;
    const t = String(Math.round(n * 100) / 100).replace('.', ',');
    return einheit ? `${t} ${einheit}` : t;
};

/** Dreiwertiges Merkmal als Wort. Alles ausser `true`/`false` ist offen. */
export const jaNeinText = (v: unknown): string => (v === true ? 'ja' : v === false ? 'nein' : NICHT_GEKLAERT);

/** „1, 4 und 10", Folgen ab drei als Spanne: „4 bis 9". */
export const nummernText = (nummern: readonly number[]): string => {
    const n = [...new Set(nummern)].sort((a, b) => a - b);
    const teile: string[] = [];
    let i = 0;
    while (i < n.length) {
        let j = i;
        while (j + 1 < n.length && n[j + 1] === n[j] + 1) j++;
        if (j - i >= 2) teile.push(`${n[i]} bis ${n[j]}`);
        else for (let k = i; k <= j; k++) teile.push(String(n[k]));
        i = j + 1;
    }
    if (teile.length <= 1) return teile.join('');
    return `${teile.slice(0, -1).join(', ')} und ${teile[teile.length - 1]}`;
};

const anzahl = (n: number, einzahl: string, mehrzahl: string): string => `${n} ${n === 1 ? einzahl : mehrzahl}`;

const adressatKurz = (liste: readonly FrageAdressat[] | null | undefined): string => {
    const a = (liste || []).filter(Boolean);
    return a.length ? a.join(', ') : 'ohne Adressat';
};

const lieferantText = (liste: readonly FrageAdressat[] | null | undefined): string => {
    const a = (liste || []).filter(Boolean);
    return a.length ? a.map(x => LIEFERANT_TITEL[x] || x).join(', ') : 'nicht festgelegt';
};

/** „Bestand, Schadstoffe und Rückbau (Kapitel E)"; endet der Titel schon
 *  mit einer Klammer, kommt das Kapitel hinein statt einer zweiten Klammer. */
export const kapitelUeberschrift = (k: KatalogKapitel): string => {
    const info = KAPITEL.find(x => x.key === k);
    if (!info) return `Kapitel ${k}`;
    const titel = katalogText(info.titel);
    return titel.endsWith(')') ? `${titel.slice(0, -1)}, Kapitel ${k})` : `${titel} (Kapitel ${k})`;
};

/** Fragetext: Katalogfragen in Schreibweise mit Umlauten, eigene Fragen wie
 *  vom Nutzer getippt. */
const frageText = (f: Planungsfrage): string => {
    const t = String(f.frage || '').trim();
    if (!t) return 'ohne Fragetext';
    return f.eigene_frage ? t : katalogText(t);
};

// ═══════════════════════════════════════════════════════════════════════════
// TITELEI
// ═══════════════════════════════════════════════════════════════════════════

const statusText = (phase: Planungsphase): string => {
    if (phase && phase.status === 'abgeschlossen') {
        const am = datumDeutsch(phase.abgeschlossen_am);
        return am ? `abgeschlossen am ${am}` : 'abgeschlossen';
    }
    return 'laufend';
};

const bauvorhabenKopf = (p: DokumentProjekt): string => {
    const name = einzeilig(p.name);
    const nummer = einzeilig(p.nummer);
    if (name && nummer) return `${name} (Projekt-Nr. ${nummer})`;
    if (name) return name;
    if (nummer) return `Projekt-Nr. ${nummer}`;
    return 'Bauvorhaben ohne Bezeichnung';
};

// ═══════════════════════════════════════════════════════════════════════════
// 1 VORHABEN
// ═══════════════════════════════════════════════════════════════════════════

const abschnittVorhaben = (e: DokumentEingang): DokumentAbschnitt => {
    const phase = e.phase;
    const m: ProjektMerkmale = (phase && phase.merkmale) || {};

    const beschreibung = absaetzeAus(phase && phase.baubeschreibung);
    const bauaufgabe = absaetzeAus(phase && phase.bauaufgabe);

    const lph = phase && typeof phase.lph === 'number' && isFinite(phase.lph) ? `Leistungsphase ${phase.lph}` : NICHT_ANGEGEBEN;
    const typ = gebaeudetypAus(m.gebaeudetyp);
    const dritter = dritterText(m.dritterNachPara4);
    const baustart = datumDeutsch(e.baustartIso);

    return {
        nummer: '1',
        titel: 'Vorhaben',
        bloecke: [],
        unterabschnitte: [
            {
                nummer: '1.1',
                titel: 'Baubeschreibung',
                bloecke: beschreibung.length
                    ? beschreibung.map(absatz)
                    : [absatz('Eine Baubeschreibung liegt nicht vor.')],
                unterabschnitte: [],
            },
            {
                nummer: '1.2',
                titel: 'Ergänzende Angaben zur Bauaufgabe',
                bloecke: bauaufgabe.length
                    ? bauaufgabe.map(absatz)
                    : [absatz('Ergänzende Angaben zur Bauaufgabe sind nicht erfasst.')],
                unterabschnitte: [],
            },
            {
                nummer: '1.3',
                titel: 'Einordnung des Vorhabens',
                bloecke: [{
                    art: 'angaben',
                    zeilen: [
                        { bezeichnung: 'Leistungsphase nach HOAI', wert: lph },
                        { bezeichnung: 'Gebäudetyp und Nutzung', wert: typ ? GEBAEUDETYP_LABEL[typ] : NICHT_GEKLAERT },
                        { bezeichnung: 'Vorgesehener Baubeginn', wert: baustart || NICHT_ANGEGEBEN },
                        {
                            bezeichnung: 'Dritter nach § 4 BaustellV',
                            wert: dritter || 'nicht erfasst; maßgeblich ist die Antwort auf Frage PL-A-02',
                        },
                    ],
                }],
                unterabschnitte: [],
            },
        ],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 2 UMFANG UND PFLICHTEN
// ═══════════════════════════════════════════════════════════════════════════

const PFLICHT_BEZEICHNUNG: Record<PflichtArt, string> = {
    vorankuendigung: 'Vorankündigung',
    sigeplan: 'Sicherheits- und Gesundheitsschutzplan',
    koordinator: 'Bestellung eines Koordinators',
    ufsa: 'Unterlage für spätere Arbeiten',
    asplan: 'Arbeits- und Sicherheitsplan bei Arbeiten in kontaminierten Bereichen',
};

/** Ergebniswort je Stand. Beim A+S-Plan ist es ein Verdacht, keine Pflicht
 *  im Sinne der BaustellV — daher eigener Wortlaut. */
export const pflichtErgebnisText = (art: PflichtArt, stand: PflichtStand): string => {
    if (stand === 'ungeklaert') return 'noch nicht geklärt';
    if (art === 'asplan') return stand === 'pflicht' ? 'Anhaltspunkte vorhanden' : 'keine Anhaltspunkte';
    return stand === 'pflicht' ? 'erforderlich' : 'nicht erforderlich';
};

/** Satz, der in Abschnitt 2.2 IMMER steht (Hausregel dreiwertig). */
export const SATZ_DREIWERTIG =
    '„Noch nicht geklärt“ bedeutet nicht „nicht erforderlich“. Solange eine Angabe fehlt, '
    + 'die das Ergebnis ändern kann, ist die Pflicht weder bejaht noch verneint; sie bleibt '
    + 'Gegenstand der Klärung.';

const anhang2Ergebnis = (z: boolean | undefined): string =>
    z === true ? 'trifft zu' : z === false ? 'ausgeschlossen' : 'noch nicht geklärt';

const abschnittPflichten = (e: DokumentEingang): DokumentAbschnitt => {
    const m: ProjektMerkmale = (e.phase && e.phase.merkmale) || {};
    const pflichten = pflichtenStand(e.phase, e.heuteIso);
    const anhang2 = schwellenwerteAusMerkmalen(m, e.heuteIso).anhang2 || [];

    const umfang: DokumentBlock = {
        art: 'angaben',
        zeilen: [
            { bezeichnung: 'Voraussichtliche Bauzeit', wert: zahlText(m.arbeitstage, 'Arbeitstage') },
            { bezeichnung: 'Höchstzahl gleichzeitig Beschäftigter', wert: zahlText(m.maxBeschaeftigte) },
            { bezeichnung: 'Umfang der Arbeiten', wert: zahlText(m.personentage, 'Personentage') },
            { bezeichnung: 'Beschäftigte mehrerer Arbeitgeber', wert: jaNeinText(m.mehrereArbeitgeber) },
            { bezeichnung: 'Unternehmer ohne Beschäftigte', wert: jaNeinText(m.unternehmerOhneBeschaeftigte) },
        ],
    };

    const pruefung: DokumentBlock[] = [
        absatz(
            'Die Pflichten nach §§ 2 und 3 BaustellV werden aus den Angaben in Abschnitt 2.1 und den '
            + 'Projektmerkmalen in Abschnitt 3 ermittelt. Das Ergebnis lautet je Pflicht „erforderlich“, '
            + '„nicht erforderlich“ oder „noch nicht geklärt“. ' + SATZ_DREIWERTIG,
        ),
        {
            art: 'tabelle',
            spalten: [
                { titel: 'Pflicht', breite: 38 },
                { titel: 'Rechtsgrundlage', breite: 30 },
                { titel: 'Ergebnis', breite: 26 },
                { titel: 'Begründung' },
            ],
            zeilen: pflichten.map(p => [
                PFLICHT_BEZEICHNUNG[p.art] || p.art,
                p.rechtsgrundlage || NICHT_ANGEGEBEN,
                pflichtErgebnisText(p.art, p.stand),
                systemText(p.begruendung) || NICHT_ANGEGEBEN,
            ]),
        },
    ];

    const offen = pflichten.filter(p => p.stand === 'ungeklaert');
    if (offen.length) {
        pruefung.push(absatz(
            `Zum Stand ${offen.length === 1 ? 'ist' : 'sind'} noch nicht geklärt: `
            + `${offen.map(p => PFLICHT_BEZEICHNUNG[p.art]).join(', ')}. `
            + 'Die fehlenden Angaben sind in der Begründung genannt.',
        ));
    }

    const vorank = pflichten.find(p => p.art === 'vorankuendigung');
    const vorankFrist = vorankuendigungFrist(e.baustartIso);
    if (vorank && vorank.stand === 'pflicht') {
        pruefung.push(absatz(vorankFrist
            ? 'Die Vorankündigung ist der zuständigen Behörde spätestens zwei Wochen vor Einrichtung '
                + `der Baustelle zu übermitteln. Bezogen auf den vorgesehenen Baubeginn am ${datumDeutsch(e.baustartIso)} `
                + `ist das spätestens der ${datumDeutsch(vorankFrist)}.`
            : 'Die Vorankündigung ist der zuständigen Behörde spätestens zwei Wochen vor Einrichtung '
                + 'der Baustelle zu übermitteln. Ein Baubeginn ist nicht angegeben; der späteste Termin '
                + 'lässt sich daher noch nicht bestimmen.'));
    }

    const zutreffend = anhang2.filter(a => a.zutreffend === true).map(a => a.nummer);
    const ausgeschlossen = anhang2.filter(a => a.zutreffend === false).map(a => a.nummer);
    const ungeklaert = anhang2.filter(a => a.zutreffend !== true && a.zutreffend !== false).map(a => a.nummer);
    const saetze: string[] = [];
    if (zutreffend.length) {
        saetze.push(zutreffend.length === 1
            ? `Nach den Projektmerkmalen trifft die Arbeit nach Anhang II Nr. ${nummernText(zutreffend)} zu.`
            : `Nach den Projektmerkmalen treffen die Arbeiten nach Anhang II Nr. ${nummernText(zutreffend)} zu.`);
    } else if (!ungeklaert.length) {
        saetze.push('Nach den Projektmerkmalen trifft keine der Arbeiten nach Anhang II zu.');
    } else {
        saetze.push('Eine Arbeit nach Anhang II ist bisher nicht als zutreffend festgestellt.');
    }
    if (ausgeschlossen.length && (zutreffend.length || ungeklaert.length)) {
        saetze.push(`Ausgeschlossen ${ausgeschlossen.length === 1 ? 'ist' : 'sind'} Nr. ${nummernText(ausgeschlossen)}.`);
    }
    if (ungeklaert.length) {
        saetze.push(
            `Noch nicht geklärt ${ungeklaert.length === 1 ? 'ist' : 'sind'} Nr. ${nummernText(ungeklaert)}; `
            + `${ungeklaert.length === 1 ? 'sie gilt' : 'sie gelten'} bis zur Klärung nicht als ausgeschlossen.`,
        );
    }

    const anhangTabelle: DokumentBlock = {
        art: 'tabelle',
        spalten: [
            { titel: 'Nr.', breite: 10 },
            { titel: 'Arbeiten nach Anhang II BaustellV' },
            { titel: 'Ergebnis', breite: 26 },
            { titel: 'Grundlage', breite: 48 },
        ],
        zeilen: anhang2.map(a => [
            String(a.nummer),
            katalogText(a.bezeichnung),
            anhang2Ergebnis(a.zutreffend),
            // Der Zusatz zur 10-t-Grenze steht schon in der Bezeichnung.
            systemText(String(a.begruendung || '').replace(/\s*Seit 01\.04\.2023 ohne 10-t-Grenze.*$/, '')) || NICHT_ANGEGEBEN,
        ]),
    };

    return {
        nummer: '2',
        titel: 'Umfang und Pflichten nach Baustellenverordnung',
        bloecke: [],
        unterabschnitte: [
            { nummer: '2.1', titel: 'Umfang der Baustelle', bloecke: [umfang], unterabschnitte: [] },
            { nummer: '2.2', titel: 'Ergebnis der Prüfung', bloecke: pruefung, unterabschnitte: [] },
            {
                nummer: '2.3',
                titel: 'Besonders gefährliche Arbeiten nach Anhang II',
                bloecke: [absatz(saetze.join(' ')), anhangTabelle],
                unterabschnitte: [],
            },
        ],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 3 PROJEKTMERKMALE
// ═══════════════════════════════════════════════════════════════════════════

const abschnittMerkmale = (e: DokumentEingang): DokumentAbschnitt => {
    const m: ProjektMerkmale = (e.phase && e.phase.merkmale) || {};
    const werte = m as Record<string, unknown>;

    const gruppen: DokumentAbschnitt[] = MERKMAL_GRUPPEN.map((g, i) => ({
        nummer: `3.${i + 1}`,
        titel: g.titel,
        bloecke: [{
            art: 'tabelle',
            spalten: [{ titel: 'Merkmal' }, { titel: 'Angabe', breite: 30 }],
            zeilen: g.keys.map(k => [MERKMAL_LABEL[String(k)] || String(k), jaNeinText(werte[String(k)])]),
        }],
        unterabschnitte: [],
    }));

    const baujahr = m.bestand === false
        ? 'entfällt, kein Bauen im Bestand'
        : typeof m.bestandBaujahr === 'number' && isFinite(m.bestandBaujahr) && m.bestandBaujahr > 0
            ? String(Math.trunc(m.bestandBaujahr))
            : NICHT_ANGEGEBEN;

    gruppen.push({
        nummer: `3.${MERKMAL_GRUPPEN.length + 1}`,
        titel: 'Maßangaben',
        bloecke: [{
            art: 'angaben',
            zeilen: [
                { bezeichnung: 'Baujahr des Bestands', wert: baujahr },
                { bezeichnung: 'Größte Baugruben- oder Grabentiefe', wert: zahlText(m.grabentiefeM, 'm') },
                { bezeichnung: 'Größte Absturzhöhe', wert: zahlText(m.absturzhoeheM, 'm') },
            ],
        }],
        unterabschnitte: [],
    });

    return {
        nummer: '3',
        titel: 'Projektmerkmale',
        bloecke: [absatz(
            'Die Projektmerkmale bestimmen, welche Fragen des Katalogs gestellt und welche Unterlagen '
            + 'angefordert werden. Jedes Merkmal ist mit „ja“, „nein“ oder „nicht geklärt“ erfasst. Ein '
            + 'nicht geklärtes Merkmal wird wie ein mögliches „ja“ behandelt, damit keine Frage entfällt, '
            + 'bevor der Sachverhalt geklärt ist.',
        )],
        unterabschnitte: gruppen,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 4 FRAGENKATALOG
// ═══════════════════════════════════════════════════════════════════════════

const istErledigt = (f: Planungsfrage): boolean => f.status === 'beantwortet' || f.status === 'gegenstandslos';

const fristZelle = (f: Planungsfrage, heuteIso: string): string => {
    const tag = datumDeutsch(f.frist_am);
    if (!tag) return 'keine Frist';
    return istUeberfaellig(f, heuteIso) ? `${tag}, abgelaufen` : tag;
};

/** Antwort, darunter die Quelle (wer, Weg, Beleg, Eingang — `quelleText`)
 *  und, nur mit Schalter, die interne Bewertung. */
const antwortZelle = (f: Planungsfrage, mitBewertung: boolean, belegTitel?: Record<string, string>): string => {
    const teile: string[] = [];
    const antwort = String(f.antwort || '').trim();
    teile.push(antwort || 'keine Antwort');
    const beleg = f.antwort_beleg_id && belegTitel ? belegTitel[f.antwort_beleg_id] : undefined;
    const quelle = quelleText(f, beleg);
    if (quelle) teile.push(quelle);
    const bewertung = String(f.bewertung || '').trim();
    if (mitBewertung && bewertung) teile.push(`Bewertung (intern): ${bewertung}`);
    return teile.join('\n');
};

/** Breiten in mm, gemessen an der Tabellenschrift (8 pt): ein Datum
 *  „15.09.2026" und „Gegenstandslos" passen ungetrennt. */
export const FRAGEN_SPALTEN: TabellenSpalte[] = [
    { titel: 'Code', breite: 14 },
    { titel: 'Frage', gewicht: 1.2 },
    { titel: 'Adressat', breite: 15 },
    { titel: 'Krit.', breite: 9, ausrichtung: 'mitte' },
    { titel: 'Status', breite: 23.5 },
    { titel: 'Gestellt', breite: 17.5 },
    { titel: 'Frist', breite: 17.5 },
    { titel: 'Antwort' },
];

const abschnittFragen = (e: DokumentEingang, mitBewertung: boolean): DokumentAbschnitt => {
    const fragen = (e.fragen || []).filter(Boolean);
    const versionRoh = e.phase ? e.phase.katalog_version : undefined;
    const fassung = typeof versionRoh === 'number' && isFinite(versionRoh) ? `in der Fassung ${versionRoh} ` : '';

    if (!fragen.length) {
        return {
            nummer: '4',
            titel: 'Fragenkatalog',
            bloecke: [absatz(
                'Für das Vorhaben ist noch kein Fragenkatalog erzeugt. Es liegen keine Fragen vor; '
                + 'der Stand der Klärung lässt sich daher noch nicht angeben.',
            )],
            unterabschnitte: [],
        };
    }

    const k = berechneKennzahlen(fragen, e.heuteIso);
    const unerledigt = fragen.filter(f => !istErledigt(f));
    const zaehle = (s: Planungsfrage['status']) => unerledigt.filter(f => f.status === s).length;

    const kennzahlen: DokumentBlock = {
        art: 'tabelle',
        spalten: [{ titel: 'Stand der Fragen' }, { titel: 'Anzahl', breite: 22, ausrichtung: 'rechts' }],
        zeilen: [
            ['Fragen insgesamt', String(k.gesamt)],
            ['beantwortet', String(k.beantwortet)],
            ['gegenstandslos', String(k.gegenstandslos)],
            ['nicht abschließend beantwortet', String(unerledigt.length)],
            ['davon noch nicht gestellt', String(zaehle('offen'))],
            ['davon gestellt, Antwort ausstehend', String(zaehle('gestellt'))],
            ['davon teilweise beantwortet', String(zaehle('teilbeantwortet'))],
            ['davon nicht beantwortet', String(zaehle('nicht_beantwortet'))],
            ['davon haftungsrelevant (Kritikalität A)', String(k.offenKritisch)],
            ['davon mit abgelaufener Frist', String(k.ueberfaellig)],
        ],
    };

    const vorkommend: FrageAdressat[] = [];
    fragen.forEach(f => (f.adressat || []).forEach(a => { if (a && !vorkommend.includes(a)) vorkommend.push(a); }));
    const reihenfolge = Object.keys(ADRESSAT_LABEL) as FrageAdressat[];
    vorkommend.sort((a, b) => reihenfolge.indexOf(a) - reihenfolge.indexOf(b));

    const uebersicht: DokumentBlock[] = [
        absatz(
            `Der Fragenkatalog ${fassung}umfasst für dieses Vorhaben ${anzahl(k.gesamt, 'Frage', 'Fragen')}. `
            + 'Die Fragen sind nach Kapiteln geordnet und an die jeweils zuständigen Beteiligten gerichtet. '
            + 'Der Stand verteilt sich wie folgt.',
        ),
        kennzahlen,
        absatz(
            'Die Spalte „Krit.“ gibt die Kritikalität an. A bezeichnet haftungsrelevante Fragen, ohne deren '
            + 'Beantwortung oder eine versendete Bedenkenanzeige die Planungsphase nicht abgeschlossen wird. '
            + 'B bezeichnet wichtige Fragen, die terminiert nachverfolgt werden, C ergänzende Fragen.',
        ),
        absatz(
            'Die Quelle nennt, wer die Antwort gegeben hat, auf welchem Weg und wann sie eingegangen ist. '
            + '„Quelle nicht erfasst“ kennzeichnet Antworten, bei denen das nicht festgehalten wurde.',
        ),
    ];
    if (vorkommend.length) {
        uebersicht.push({
            art: 'tabelle',
            spalten: [{ titel: 'Kürzel', breite: 20 }, { titel: 'Adressat' }],
            zeilen: vorkommend.map(a => [a, katalogText(ADRESSAT_LABEL[a] || a)]),
        });
    }
    if (mitBewertung) {
        uebersicht.push(absatz(
            'Diese Fassung enthält in der Spalte „Antwort“ zusätzlich die interne Bewertung des '
            + 'Koordinators. Sie ist nicht zur Weitergabe an Dritte bestimmt.',
        ));
    }

    const kapitel: DokumentAbschnitt[] = gruppiereNachKapitel(fragen).map((g, i) => ({
        nummer: `4.${i + 2}`,
        titel: kapitelUeberschrift(g.kapitel),
        bloecke: [{
            art: 'tabelle',
            spalten: FRAGEN_SPALTEN,
            zeilen: g.fragen.map(f => [
                String(f.code || '').trim() || 'ohne Code',
                frageText(f),
                adressatKurz(f.adressat),
                String(f.kritikalitaet || '').trim() || NICHT_ANGEGEBEN,
                STATUS_LABEL[f.status] || String(f.status || NICHT_ANGEGEBEN),
                datumDeutsch(f.gestellt_am) || 'nicht gestellt',
                fristZelle(f, e.heuteIso),
                antwortZelle(f, mitBewertung, e.belegTitel),
            ]),
        }],
        unterabschnitte: [],
    }));

    return {
        nummer: '4',
        titel: 'Fragenkatalog',
        bloecke: [],
        unterabschnitte: [
            { nummer: '4.1', titel: 'Stand der Klärung', bloecke: uebersicht, unterabschnitte: [] },
            ...kapitel,
        ],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 5 MITGELTENDE UNTERLAGEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Welche Zeilen der Checkliste ins Dokument gehen:
 *
 *   5.1 — erforderlich (Merkmal ausdruecklich wahr oder im SiGe-Plan genannt)
 *         ODER schon angefasst (gespeicherter Eintrag). Angefasst heisst auch
 *         „nicht erforderlich" mit Begruendung: das ist eine dokumentierte
 *         Entscheidung und gehoert gerade in die Akte.
 *   5.2 — nur ueber ein noch NICHT geklaertes Merkmal relevant und nie
 *         angefasst. Dreiwertig: sie fallen nicht weg, sie stehen getrennt,
 *         damit 5.1 nicht mit Vermutungen gefuellt wird.
 *
 * Katalogdokumente ohne jeden Bezug (kein Merkmal, kein SiGe-Plan, kein
 * Eintrag) bleiben draussen — der ganze Katalog ist keine Projektakte.
 */
export const unterlagenAuswahl = (zeilen: readonly UnterlagenZeile[]): {
    bearbeitet: UnterlagenZeile[];
    zuKlaeren: UnterlagenZeile[];
} => ({
    bearbeitet: zeilen.filter(z => z.erforderlich || !!z.eintrag),
    zuKlaeren: zeilen.filter(z => !z.erforderlich && !z.eintrag && z.relevanz === 'zu_klaeren'),
});

const unterlagenStatusZelle = (z: UnterlagenZeile): string => {
    const label = UNTERLAGEN_STATUS_LABEL[z.status] || String(z.status);
    const seit = z.eintrag ? datumDeutsch(z.eintrag.status_am) : '';
    return z.status === 'liegt_vor' && seit ? `${label} seit ${seit}` : label;
};

const unterlagenFristZelle = (z: UnterlagenZeile, heuteIso: string): string => {
    const tag = z.eintrag ? datumDeutsch(z.eintrag.frist_am) : '';
    if (!tag) return 'keine Frist';
    return z.eintrag && unterlagenFristUeberfaellig(z.eintrag, heuteIso) ? `${tag}, abgelaufen` : tag;
};

const unterlagenBemerkung = (z: UnterlagenZeile): string => {
    const teile: string[] = [];
    const bemerkung = z.eintrag ? String(z.eintrag.bemerkung || '').trim() : '';
    if (bemerkung) teile.push(bemerkung);
    if (z.relevanz === 'erforderlich' && z.merkmale.length) {
        teile.push(`Erforderlich wegen: ${z.merkmale.map(merkmalText).join(', ')}`);
    }
    if (z.sigeSchritte.length) teile.push(`Im SiGe-Plan genannt: ${z.sigeSchritte.join(', ')}`);
    return teile.join('\n') || 'keine';
};

/** Abschnitt 5.1 ohne gelesenen SiGe-Plan — ehrlich statt eines behaupteten Bezugs. */
export const SATZ_SIGEPLAN_KEINER = 'Ein SiGe-Plan lag nicht vor; berücksichtigt sind nur die Projektmerkmale.';
export const SATZ_SIGEPLAN_NICHT_LESBAR = 'Der SiGe-Plan konnte nicht gelesen werden; berücksichtigt sind nur die Projektmerkmale.';

const abschnittUnterlagen = (e: DokumentEingang): DokumentAbschnitt => {
    const planGelesen = e.sigePlan === 'gelesen';
    const zeilen = baueUnterlagenZeilen({
        merkmale: (e.phase && e.phase.merkmale) || {},
        eintraege: e.unterlagen || [],
        sigeSchritte: planGelesen ? e.sigeSchritte || [] : [],
    });
    const { bearbeitet, zuKlaeren } = unterlagenAuswahl(zeilen);
    // Ohne gelesenen Plan sagt 5.1 das — sonst laese man „steht nicht im
    // SiGe-Plan", wo es „nicht gelesen" heissen muss.
    const ohnePlan = planGelesen ? '' : ` ${e.sigePlan === 'keiner' ? SATZ_SIGEPLAN_KEINER : SATZ_SIGEPLAN_NICHT_LESBAR}`;
    const grundlage = planGelesen ? 'nach den Projektmerkmalen oder dem SiGe-Plan' : 'nach den Projektmerkmalen';

    const teil1: DokumentBlock[] = bearbeitet.length
        ? [
            absatz(
                `Aufgeführt sind die Unterlagen, die ${grundlage} `
                + 'erforderlich sind, sowie alle Unterlagen, deren Stand bereits bearbeitet wurde. Eine als '
                + `„nicht erforderlich“ eingestufte Unterlage ist mit Begründung vermerkt.${ohnePlan}`,
            ),
            {
                art: 'tabelle',
                spalten: [
                    { titel: 'Unterlage' },
                    { titel: 'Lieferant', breite: 26 },
                    { titel: 'Status', breite: 24 },
                    { titel: 'Angefordert', breite: 19.5 },
                    { titel: 'Frist', breite: 19 },
                    { titel: 'Bemerkung', breite: 40 },
                ],
                zeilen: bearbeitet.map(z => [
                    String(z.name || '').trim() || 'ohne Bezeichnung',
                    lieferantText(z.lieferant),
                    unterlagenStatusZelle(z),
                    (z.eintrag && datumDeutsch(z.eintrag.angefordert_am)) || 'nicht angefordert',
                    unterlagenFristZelle(z, e.heuteIso),
                    unterlagenBemerkung(z),
                ]),
            },
        ]
        : [absatz(`Es sind keine Unterlagen als erforderlich festgestellt oder bereits bearbeitet.${ohnePlan}`)];

    const teil2: DokumentBlock[] = zuKlaeren.length
        ? [
            absatz(
                'Bei den folgenden Unterlagen hängt das Erfordernis von Projektmerkmalen ab, die noch nicht '
                + 'geklärt sind. Sie gelten bis zur Klärung als möglicherweise erforderlich.',
            ),
            {
                art: 'tabelle',
                spalten: [
                    { titel: 'Unterlage' },
                    { titel: 'Lieferant', breite: 30 },
                    { titel: 'Abhängig von', breite: 62 },
                ],
                zeilen: zuKlaeren.map(z => [
                    String(z.name || '').trim() || 'ohne Bezeichnung',
                    lieferantText(z.lieferant),
                    z.merkmale.map(merkmalText).join(', ') || NICHT_ANGEGEBEN,
                ]),
            },
        ]
        : [absatz('Es gibt keine Unterlagen, deren Erfordernis von einem noch nicht geklärten Projektmerkmal abhängt.')];

    return {
        nummer: '5',
        titel: 'Mitgeltende Unterlagen',
        bloecke: [],
        unterabschnitte: [
            { nummer: '5.1', titel: 'Erforderliche und bearbeitete Unterlagen', bloecke: teil1, unterabschnitte: [] },
            { nummer: '5.2', titel: 'Unterlagen mit noch nicht geklärtem Erfordernis', bloecke: teil2, unterabschnitte: [] },
        ],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 6 HINWEISE UND BEDENKENANZEIGEN
// ═══════════════════════════════════════════════════════════════════════════

const reaktionZelle = (h: Planungshinweis): string => {
    const text = String(h.reaktion || '').trim();
    const am = datumDeutsch(h.reaktion_am);
    if (!text) return am ? `Reaktion am ${am}, ohne Wortlaut erfasst` : 'keine Reaktion dokumentiert';
    return am ? `${text}\n(am ${am})` : text;
};

const abschnittHinweise = (e: DokumentEingang): DokumentAbschnitt => {
    const liste = [...(e.hinweise || [])].filter(Boolean).sort((a, b) => (a.nummer || 0) - (b.nummer || 0));
    if (!liste.length) {
        return {
            nummer: '6',
            titel: 'Hinweise und Bedenkenanzeigen',
            bloecke: [absatz('Es liegen keine Hinweise oder Bedenkenanzeigen vor.')],
            unterabschnitte: [],
        };
    }
    const entwuerfe = liste.filter(h => !h.versendet_am).length;
    const einleitung = 'Aufgeführt sind alle Hinweise, Bedenkenanzeigen und Gefahrenanzeigen, die in der '
        + 'Planungsphase erstellt wurden. Gegenüber dem Bauherrn wirksam ist nur eine versendete Anzeige.'
        + (entwuerfe === 1 ? ' Ein Entwurf ist als „nicht versendet“ ausgewiesen.' : '')
        + (entwuerfe > 1 ? ` Als „nicht versendet“ ausgewiesen sind ${entwuerfe} Entwürfe.` : '');
    return {
        nummer: '6',
        titel: 'Hinweise und Bedenkenanzeigen',
        bloecke: [
            absatz(einleitung),
            {
                art: 'tabelle',
                spalten: [
                    { titel: 'Nr.', breite: 8 },
                    { titel: 'Art', breite: 26 },
                    { titel: 'Betreff' },
                    { titel: 'Empfänger', breite: 26 },
                    { titel: 'Versendet', breite: 18 },
                    { titel: 'Betroffene Fragen', breite: 21 },
                    { titel: 'Reaktion', breite: 34 },
                ],
                zeilen: liste.map(h => [
                    String(h.nummer || ''),
                    HINWEIS_ART_LABEL[h.art] || String(h.art || NICHT_ANGEGEBEN),
                    String(h.betreff || '').trim() || 'ohne Betreff',
                    String(h.empfaenger_name || '').trim() || NICHT_ANGEGEBEN,
                    datumDeutsch(h.versendet_am) || 'nicht versendet',
                    (h.frage_codes || []).filter(Boolean).join(', ') || 'keine',
                    reaktionZelle(h),
                ]),
            },
        ],
        unterabschnitte: [],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 7 OFFENE PUNKTE
// ═══════════════════════════════════════════════════════════════════════════

const abschnittOffenePunkte = (e: DokumentEingang): DokumentAbschnitt => {
    const fragen = (e.fragen || []).filter(Boolean);
    const kopf = { nummer: '7', titel: 'Offene Punkte zur Übergabe in die Ausführungsphase', unterabschnitte: [] };

    if (!fragen.length) {
        return {
            ...kopf,
            bloecke: [absatz(
                'Da noch kein Fragenkatalog erzeugt ist, lässt sich der Umfang der offenen Punkte nicht feststellen.',
            )],
        };
    }

    const offen = restrisikoliste(fragen, e.hinweise || [], e.heuteIso);
    if (!offen.length) {
        return {
            ...kopf,
            bloecke: [absatz('Es bestehen keine offenen Punkte. Alle Fragen sind beantwortet oder als gegenstandslos dokumentiert.')],
        };
    }

    const freigabe = pruefeFreigabe(fragen, e.hinweise || []);
    const kritisch = freigabe.blockierendeCodes.length + freigabe.gedeckteCodes.length;
    const saetze: string[] = [
        `Zum Stand ${offen.length === 1 ? 'ist eine Frage' : `sind ${offen.length} Fragen`} nicht abschließend beantwortet. `
        + `${offen.length === 1 ? 'Sie ist' : 'Sie sind'} dem Koordinator der Ausführungsphase zu übergeben und dort weiter zu verfolgen.`,
    ];
    if (!kritisch) {
        saetze.push('Haftungsrelevante Fragen (Kritikalität A) sind nicht darunter.');
    } else {
        saetze.push(`Davon ${kritisch === 1 ? 'ist eine' : `sind ${kritisch}`} haftungsrelevant (Kritikalität A).`);
        // Kein Satz beginnt mit einer Ziffer.
        const gedeckt = freigabe.gedeckteCodes.length;
        if (gedeckt) {
            saetze.push(gedeckt === 1
                ? 'Eine dieser Fragen ist durch eine versendete Bedenken- oder Gefahrenanzeige gedeckt.'
                : `Durch eine versendete Bedenken- oder Gefahrenanzeige gedeckt sind ${gedeckt} dieser Fragen.`);
        }
        const offenA = freigabe.blockierendeCodes;
        if (offenA.length) {
            // Bis acht Codes im Satz; eine laengere Liste liest niemand im
            // Fliesstext — sie steht ohnehin in der Tabelle darunter.
            const liste = offenA.length <= 8 ? ` (${offenA.join(', ')})` : '';
            saetze.push(offenA.length === 1
                ? `Eine dieser Fragen ist weder beantwortet noch durch eine versendete Anzeige gedeckt${liste}.`
                : `Weder beantwortet noch durch eine versendete Anzeige gedeckt sind ${offenA.length} dieser Fragen${liste}.`);
        }
    }

    const nachCode = new Map(fragen.map(f => [f.code, f]));
    return {
        ...kopf,
        bloecke: [
            absatz(saetze.join(' ')),
            {
                art: 'tabelle',
                spalten: [
                    { titel: 'Code', breite: 14 },
                    { titel: 'Frage' },
                    { titel: 'Status', breite: 23.5 },
                    { titel: 'Krit.', breite: 9, ausrichtung: 'mitte' },
                    { titel: 'Gedeckt durch Anzeige Nr.', breite: 24 },
                    { titel: 'Frist abgelaufen am', breite: 23 },
                ],
                zeilen: offen.map(r => {
                    const f = nachCode.get(r.code);
                    const abgelaufen = datumDeutsch(r.fristAbgelaufenAm);
                    const frist = abgelaufen
                        || (f && datumDeutsch(f.frist_am) ? 'nicht abgelaufen' : 'keine Frist');
                    return [
                        String(r.code || '').trim() || 'ohne Code',
                        f ? frageText(f) : katalogText(r.frage),
                        STATUS_LABEL[r.status] || String(r.status),
                        String(r.kritikalitaet || '') || NICHT_ANGEGEBEN,
                        r.hinweisNummern.length ? r.hinweisNummern.join(', ') : 'keine',
                        frist,
                    ];
                }),
            },
        ],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 8 ABSCHLUSS
// ═══════════════════════════════════════════════════════════════════════════

export const SATZ_NICHT_ABGESCHLOSSEN = 'Die Planungsphase ist nicht abgeschlossen.';

const abschnittAbschluss = (e: DokumentEingang, standDatum: string): DokumentAbschnitt => {
    const phase = e.phase;
    const offen = (e.fragen || []).filter(f => f && !istErledigt(f)).length;
    let text: string;
    if (phase && phase.status === 'abgeschlossen') {
        const am = datumDeutsch(phase.abgeschlossen_am);
        text = am ? `Die Planungsphase wurde am ${am} abgeschlossen.` : 'Die Planungsphase ist abgeschlossen.';
        if (offen) text += ' Die in Abschnitt 7 aufgeführten offenen Punkte gehen in die Ausführungsphase über.';
    } else {
        text = `${SATZ_NICHT_ABGESCHLOSSEN} Diese Dokumentation gibt den Stand vom ${standDatum} wieder `
            + 'und wird bei Fortschreibung ersetzt.';
    }
    const name = einzeilig(e.bearbeiter);
    return {
        nummer: '8',
        titel: 'Abschluss',
        bloecke: [
            absatz(text),
            {
                art: 'zeichnung',
                felder: [
                    { beschriftung: 'Ort, Datum' },
                    { beschriftung: 'Koordinator nach BaustellV', ...(name ? { name } : {}) },
                ],
            },
        ],
        unterabschnitte: [],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// GESAMTMODELL
// ═══════════════════════════════════════════════════════════════════════════

export const baueDokumentModell = (e: DokumentEingang, optionen: DokumentOptionen = {}): DokumentModell => {
    const mitBewertung = optionen.mitInternerBewertung === true;
    const phase = e.phase;
    const p: DokumentProjekt = e.projekt || {};
    const standDatum = datumDeutsch(e.heuteIso) || NICHT_ANGEGEBEN;
    const version = phase && typeof phase.katalog_version === 'number' && isFinite(phase.katalog_version)
        ? `Fassung ${phase.katalog_version}`
        : NICHT_ANGEGEBEN;

    const titelei: Angabe[] = [
        { bezeichnung: 'Bauvorhaben', wert: oder(p.name, NICHT_ANGEGEBEN) },
        { bezeichnung: 'Projektnummer', wert: oder(p.nummer, NICHT_ANGEGEBEN) },
        { bezeichnung: 'Anschrift der Baustelle', wert: oder(p.adresse, NICHT_ANGEGEBEN) },
        { bezeichnung: 'Bauherr', wert: oder(p.bauherr, NICHT_ANGEGEBEN) },
        { bezeichnung: 'Koordinator', wert: oder(e.bearbeiter, NICHT_ANGEGEBEN) },
        { bezeichnung: 'Stand', wert: standDatum },
        { bezeichnung: 'Fragenkatalog', wert: version },
        { bezeichnung: 'Status der Planungsphase', wert: statusText(phase) },
    ];
    if (mitBewertung) {
        titelei.push({ bezeichnung: 'Fassung', wert: 'mit interner Bewertung des Koordinators, nicht zur Weitergabe bestimmt' });
    }

    const vorbemerkung = [
        `Diese Dokumentation gibt den Stand der Sicherheits- und Gesundheitsschutzkoordination in der `
        + `Planungsphase des Bauvorhabens zum ${standDatum} wieder. Sie fasst die Angaben zum Vorhaben, die `
        + 'Prüfung nach der Baustellenverordnung, die Projektmerkmale, den Stand des Fragenkatalogs, die '
        + 'mitgeltenden Unterlagen sowie die Hinweise und Bedenkenanzeigen zusammen.',
        'Grundlage sind die Angaben des Bauherrn und der Planungsbeteiligten, soweit sie dem Koordinator bis '
        + 'zum Stand vorlagen. Angaben, die noch nicht geklärt sind, werden als solche ausgewiesen. Eine noch '
        + 'nicht geklärte Angabe ist nicht als Verneinung zu verstehen; eine davon abhängige Pflicht gilt erst '
        + 'dann als nicht gegeben, wenn die Angabe vorliegt.',
    ];

    return {
        titel: DOKUMENT_TITEL,
        untertitel: DOKUMENT_UNTERTITEL,
        bauvorhaben: bauvorhabenKopf(p),
        standDatum,
        titelei,
        vorbemerkung,
        mitInternerBewertung: mitBewertung,
        abschnitte: [
            abschnittVorhaben(e),
            abschnittPflichten(e),
            abschnittMerkmale(e),
            abschnittFragen(e, mitBewertung),
            abschnittUnterlagen(e),
            abschnittHinweise(e),
            abschnittOffenePunkte(e),
            abschnittAbschluss(e, standDatum),
        ],
    };
};

/** Eintrag der Gliederung — Grundlage des Inhaltsverzeichnisses. */
export interface GliederungsEintrag {
    nummer: string;
    titel: string;
    ebene: number;
}

/** Alle Ueberschriften in Lesereihenfolge, Ebene 1 = Hauptabschnitt. */
export const gliederung = (modell: DokumentModell): GliederungsEintrag[] => {
    const aus: GliederungsEintrag[] = [];
    const lauf = (liste: readonly DokumentAbschnitt[], ebene: number) => {
        (liste || []).forEach(a => {
            aus.push({ nummer: a.nummer, titel: a.titel, ebene });
            lauf(a.unterabschnitte, ebene + 1);
        });
    };
    lauf(modell.abschnitte, 1);
    return aus;
};
